// Copyright 2021-2022 zcloak authors & contributors
// SPDX-License-Identifier: Apache-2.0

import Check from '@mui/icons-material/Check';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  lighten,
  Stack,
  Step,
  StepIconProps,
  StepLabel,
  Stepper,
  styled,
  Typography
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import ButtonEnable from '../../components/ButtonEnable';
import DialogHeader from '../../components/DialogHeader';
import { endpoint } from '../../config';
import { event } from '../../hooks/events';
import { Phase } from '../../hooks/usePhase';
import { useToggle } from '../../hooks/useToggle';
import { ZkidWalletContext } from '../../ZkidWallet';
import IconFail from './icon_fail.svg';
import IconSuccess from './icon_success.svg';

const Content = styled(Box)({
  width: '100%',
  padding: '32px 24px',
  background: '#EAEBEF',
  borderRadius: 10,
  marginBottom: 20
});

const QontoStepIconRoot = styled('div')<{ ownerState: { active?: boolean } }>(({ ownerState }) => ({
  color: '#483BE8',
  display: 'flex',
  width: 22,
  height: 22,
  alignItems: 'center',
  justifyContent: 'center',
  ...(ownerState.active && {
    color: '#483BE8'
  }),
  '& .QontoStepIcon-completedIcon': {
    color: '#483BE8',
    zIndex: 1,
    fontSize: 18
  },
  '& .QontoStepIcon-circle': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'currentColor'
  }
}));

function QontoStepIcon(props: StepIconProps) {
  const { active, className, completed } = props;

  return (
    <QontoStepIconRoot className={className} ownerState={{ active }}>
      {completed ? (
        <Check className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

function ButtonVerify({ phases }: { phases?: Phase }) {
  const [open, toggleOpen] = useToggle();
  const [step, setStep] = useState(0);
  const { didUrl, generateZkp } = useContext(ZkidWalletContext);
  const [proof, setProof] = useState<string>();
  const [proofDetail, toggleProofDetail] = useToggle();
  const [status, setStatus] = useState<{ success: false; message: string } | { success: true }>();

  useEffect(() => {
    if (!open) {
      setStatus(undefined);
      setProof(undefined);
    }

    event.emit('phase_refresh');
  }, [open]);

  const requestzkpVerify = async (data: {
    starkproof: string;
    outputs: string[];
    user_did: string;
  }) => {
    const res = await fetch(`${endpoint}/verify_zkproof`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (res.ok) {
      return res.json();
    }
  };

  const handleClick = useCallback(() => {
    toggleOpen();
    didUrl &&
      generateZkp().then((res) => {
        if (!res) {
          setStatus({ success: false, message: 'Generate proof failed.' });

          return;
        }

        setStep(1);
        setProof(JSON.stringify(res));
        requestzkpVerify({
          starkproof: res.starkproof as string,
          outputs: res.outputs.map((item: any) => `${item}`),
          user_did: didUrl
        }).then((res) => {
          setStep(2);

          if (res.status !== 0) {
            setStatus({
              success: false,
              message: res.message
            });
          } else {
            setStatus({ success: true });
          }
        });
      });
  }, [toggleOpen, didUrl, generateZkp]);

  return (
    <>
      <ButtonEnable
        disabled={phases && !phases[0]}
        onClick={handleClick}
        size="large"
        variant="contained"
      >
        Verify Your Guess via zk-Proof
      </ButtonEnable>
      {phases && !phases[0] && (
        <Stack
          direction="row"
          justifyContent="center"
          spacing={1}
          sx={{
            marginTop: 1.5,
            fontSize: 18,
            fontWeight: 500,
            color: '#E8363E'
          }}
        >
          <img src={IconFail} width={24} />
          <span>Sorry, you did not share this VC in the right way in Phase I.</span>
        </Stack>
      )}
      {open && (
        <Dialog
          PaperProps={{
            sx: {
              background: status
                ? status.success
                  ? `linear-gradient(330deg, ${lighten('#FFEDB7', 0.6)} 0%, ${lighten(
                      '#FFA6CB',
                      0.6
                    )} 100%)`
                  : `linear-gradient(180deg, ${lighten('#F5A5AF', 0.4)} 0%, rgb(255,255,255) 100%)`
                : undefined
            }
          }}
          maxWidth="sm"
          onClose={toggleOpen}
          open={open}
        >
          <DialogHeader onClose={toggleOpen}>Verifying</DialogHeader>
          <DialogContent sx={{ minWidth: 600 }}>
            {proofDetail ? (
              <Content>
                <Box
                  sx={{
                    display: '-webkit-box',
                    overflow: 'hidden',
                    '-webkit-box-orient': 'vertical',
                    '-webkit-line-clamp': '6'
                  }}
                >
                  {proof}
                </Box>
              </Content>
            ) : status ? (
              status.success ? (
                <Stack alignItems="center" my={7} spacing={3}>
                  <img src={IconSuccess} />
                  <Typography textAlign="center" variant="h5">
                    Congratulations！The team you guessed is in the semi-finals.
                  </Typography>
                </Stack>
              ) : (
                <Stack alignItems="center" my={7} spacing={3}>
                  <img src={IconFail} />
                  <Typography textAlign="center" variant="h5">
                    {status.message}
                  </Typography>
                </Stack>
              )
            ) : (
              <>
                <Content>
                  <Stepper activeStep={step} orientation="vertical">
                    <Step>
                      <StepLabel StepIconComponent={QontoStepIcon}>
                        Zero Knowledge Proof Operations in your wallet
                      </StepLabel>
                    </Step>
                    <Step>
                      <StepLabel StepIconComponent={QontoStepIcon}>
                        Verify your Proof {proof ? proof.slice(0, 30) : ''}
                      </StepLabel>
                    </Step>
                  </Stepper>
                </Content>
                {!status && (
                  <Typography textAlign="center">It may take 3-10 seconds to verify…</Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: proofDetail ? 'center' : 'space-between' }}>
            {proofDetail ? (
              <Button onClick={toggleProofDetail} size="large" variant="contained">
                Back
              </Button>
            ) : (
              <>
                <Button
                  disabled={!proof}
                  onClick={toggleProofDetail}
                  size="large"
                  sx={{ width: '50%' }}
                  variant="contained"
                >
                  View Proof Details
                </Button>
                <Button
                  disabled={step < 2}
                  onClick={toggleOpen}
                  size="large"
                  sx={{ width: '50%' }}
                  variant="contained"
                >
                  Finish
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export default ButtonVerify;
